import { useState, useEffect } from 'react'
import { collection, query, where, getDocs } from 'firebase/firestore'
import { db } from '@/config/firebase'
import { AnalyticsData } from '@/types/analytics'

export function useAnalytics(orgId: string | undefined) {
    const [analytics, setAnalytics] = useState<AnalyticsData | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<Error | null>(null)

    useEffect(() => {
        if (!orgId) {
            setAnalytics(null)
            setLoading(false)
            return
        }

        const fetchAnalytics = async () => {
            try {
                setLoading(true)

                // Fetch spaces
                const spacesQuery = query(collection(db, 'spaces'), where('orgId', '==', orgId))
                const spacesSnapshot = await getDocs(spacesQuery)
                const totalSpaces = spacesSnapshot.size

                // Fetch bookings
                const bookingsQuery = query(collection(db, 'bookings'), where('orgId', '==', orgId))
                const bookingsSnapshot = await getDocs(bookingsQuery)
                const bookings = bookingsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))

                const totalBookings = bookings.length
                const totalRevenue = bookings.reduce((sum: number, b: any) => sum + (b.totalPrice || 0), 0)

                // Calculate approval rate
                const approvedBookingsCount = bookings.filter((b: any) => b.status === 'CONFIRMED' || b.status === 'COMPLETED').length
                const approvalRate = totalBookings > 0 ? (approvedBookingsCount / totalBookings) * 100 : 0

                // Calculate occupancy rate (simplified: bookings / (spaces * days in month * 8 hours per day))
                const occupancyRate = totalSpaces > 0 ? (totalBookings / (totalSpaces * 30 * 8)) * 100 : 0

                // Popular spaces
                const spaceBookingCounts = bookings.reduce((acc: any, b: any) => {
                    acc[b.spaceId] = (acc[b.spaceId] || 0) + 1
                    return acc
                }, {})

                const popularSpaces = Object.entries(spaceBookingCounts)
                    .sort(([, a]: any, [, b]: any) => b - a)
                    .slice(0, 5)
                    .map(([spaceId, count]: any) => ({
                        spaceId,
                        spaceName: 'Espaço',
                        bookingCount: count
                    }))

                // Bookings by status (synchronizing with real status values)
                const bookingsByStatus = bookings.reduce((acc: any, b: any) => {
                    const status = b.status || 'DRAFT'
                    acc[status] = (acc[status] || 0) + 1
                    return acc
                }, {
                    CONFIRMED: 0,
                    PENDING_APPROVAL: 0,
                    PENDING_PAYMENT: 0,
                    CANCELED: 0,
                    COMPLETED: 0,
                    DRAFT: 0
                })

                // Basic revenue by month
                const revenueByMonthMap = bookings.reduce((acc: any, b: any) => {
                    if (b.status === 'CONFIRMED' || b.status === 'COMPLETED') {
                        const date = b.start?.toDate?.() || new Date()
                        const key = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`
                        acc[key] = (acc[key] || 0) + (b.totalPrice || 0)
                    }
                    return acc
                }, {})

                const revenueByMonth = Object.entries(revenueByMonthMap)
                    .map(([month, revenue]: any) => ({ month, revenue }))
                    .sort((a, b) => a.month.localeCompare(b.month))
                    .slice(-6) // Last 6 months

                setAnalytics({
                    totalSpaces,
                    totalBookings,
                    totalRevenue,
                    approvalRate,
                    occupancyRate,
                    popularSpaces,
                    revenueByMonth,
                    bookingsByStatus
                })

                setError(null)
            } catch (err) {
                console.error('Error fetching analytics:', err)
                setError(err as Error)
            } finally {
                setLoading(false)
            }
        }

        fetchAnalytics()
    }, [orgId])

    return { analytics, loading, error }
}
